import React, { useEffect, useMemo } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  useFilterState,
  useScrollPosition,
  useSortingState,
} from "../../hooks/useScrollPosition";
import { columns } from "./columnsConfig/trades";
import { globalFilterFn } from "./tradesGlobalFilterFn";
import TableContainer from "./TableContainer";

const TradesTable = ({
  data,
  title,
  globalFilter: initialGlobalFilter = "",
  setGlobalFilter: setParentGlobalFilter,
  sorting: initialSorting = [{ id: "StrategyType", desc: false }],
  setSorting: setParentSorting,
  scrollPosition: initialScrollPosition = 0,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // State Management
  const [savedGlobalFilter, setSavedGlobalFilter] = useFilterState(
    "tradesTableGlobalFilter",
    initialGlobalFilter,
  );
  const [sorting, setSorting] = useSortingState(
    "tradesTableSorting",
    initialSorting,
  );
  const scrollPositionHook = useScrollPosition(
    "tradesTableScrollPosition",
    initialScrollPosition,
    '[class*="MuiTableContainer-root"]',
  );

  // Sync State with Parent
  useEffect(() => {
    if (setParentGlobalFilter) {
      setParentGlobalFilter(savedGlobalFilter);
    }
  }, [savedGlobalFilter, setParentGlobalFilter]);

  useEffect(() => {
    if (setParentSorting) {
      setParentSorting(sorting);
    }
  }, [sorting, setParentSorting]);

  const tableConfig = useMemo(
    () => ({
      enableColumnDragging: false,
      enableSorting: true,
      enableColumnOrdering: false,
      enablePagination: false,
      enableStickyHeader: true,
      enableStickyFooter: true, // Ensures footer sticks to the bottom
      enableColumnResizing: false,
      enableBottomToolbar: false,
      enablePinning: true,
      columnResizeMode: "onChange",
      enableTopToolbar: false,
      enableColumnActions: false,
      enableRowVirtualization: true,
      displayColumnDefOptions: {
        "mrt-row-numbers": {
          enableColumnOrdering: false,
          enableSorting: false,
        },
      },
      sortingDisplay: "none",
      enableGlobalFilter: true,
      // MRT stores globalFilterFn as a STRING KEY and resolves the actual
      // function via tableOptions.filterFns[key] at render time - it does
      // NOT accept a raw function here directly (that silently fails the
      // lookup and falls back to MRT's default "fuzzy" matching).
      filterFns: { tradesGlobalFilter: globalFilterFn },
      globalFilterFn: "tradesGlobalFilter",
      muiTableBodyRowProps: {
        sx: {
          backgroundColor: theme.palette.background.paper,
          padding: "0px",
          height: {
            xs: "30px", // Slightly taller rows for mobile readability
            sm: "25px",
          },
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: theme.palette.grey[100],
          },
          "&:nth-of-type(odd)": {
            backgroundColor: theme.palette.grey[100],
          },
        },
      },
      muiTableFooterRowProps: {
        sx: {
          backgroundColor: theme.palette.primary.dark,
          fontWeight: 600,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased zIndex to ensure visibility over content
          borderTop: `2px solid ${theme.palette.divider}`,
          boxSizing: "border-box",
          height: "40px", // Consistent footer height
          fontSize: {
            xs: "0.7rem",
            sm: "0.8rem",
          },
        },
      },
      muiTableProps: {
        sx: {
          width: "100%", // Use full width instead of minWidth
          tableLayout: "auto", // Allow columns to adjust dynamically
          "& .MuiTableHead-root": {
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiTableHead-root .MuiTableRow-root": {
            height: "auto",
            minHeight: {
              xs: "30px",
              sm: "35px",
            },
          },
          "& .MuiTableFooter-root": {
            borderTop: `2px solid ${theme.palette.divider}`,
            position: "sticky",
            bottom: 0,
            zIndex: 100, // Ensure footer stays above other content
            backgroundColor: theme.palette.primary.dark,
          },
          "& .MuiTableFooter-root .MuiTableRow-root": {
            height: "40px",
          },
        },
      },
      state: {
        globalFilter: savedGlobalFilter,
        sorting: sorting,
      },
      onGlobalFilterChange: setSavedGlobalFilter,
      onSortingChange: setSorting,
      initialState: {
        sorting: [
          { id: "StrategyType", desc: false },
          { id: "StrategyName", desc: false },
          { id: "security_name", desc: false },
        ],
        density: "compact",
        showGlobalFilter: true,
        globalFilter: savedGlobalFilter,
        columnPinning: isDesktop
          ? {
              left: ["StrategyType", "StrategyName", "security_name"],
            }
          : {
              left: ["StrategyName"], // Pin fewer columns on mobile for better usability
            },
      },
    }),
    [
      theme,
      isDesktop,
      savedGlobalFilter,
      sorting,
      scrollPositionHook.scrollPosition,
    ],
  );

  // Render
  return (
    <TableContainer
      title={title}
      data={data}
      columnsConfig={columns}
      globalFilter={savedGlobalFilter}
      setGlobalFilter={setSavedGlobalFilter}
      sorting={sorting}
      setSorting={setSorting}
      scrollPosition={scrollPositionHook.scrollPosition}
      tableConfig={tableConfig}
    />
  );
};

export default TradesTable;
